/**
 * Ajout AGORA (absent du bundle de référence) – NotificationsPanel : panneau « Notifications » rattaché au bouton
 * cloche du dock (10 dernières notifications, lues et non lues). Même famille que les panneaux du dock : verre
 * `ag-glass ag-glass-strong` (style Liquid Glass `.ag-dock-pop > .ag-glass` du design), boutons `ag-btn` ; mise en
 * page complémentaire `agx-notif…` dans core/app.css (jetons uniquement).
 * Aucun libellé en dur : tous les textes sont fournis, déjà traduits, par l'appelant.
 * Clavier : Tab ou ↑ ↓ (Début / Fin) entre les notifications, Entrée pour ouvrir (l'événement est transmis : Ctrl/⌘ +
 * Entrée = nouvelle page côté appelant), Échap pour fermer (onClose).
 */
import React from 'react';
import { cx, h, Icon, type IconName } from './base';

export interface NotificationItem {
  id: string;
  icon: IconName;
  /** Message traduit (« Camille Martin vous a affecté l'incident … »). */
  message: string;
  /** Auteur de l'événement (nom affiché). */
  author?: string;
  /** Temps relatif (« il y a 5 min ») ; date complète en infobulle et en attribut datetime. */
  time: string;
  dateTime?: string;
  dateLabel?: string;
  /** Code de la filiale de l'objet (FR01…). */
  entity?: string;
  unread?: boolean;
}

export interface NotificationsPanelProps {
  title: string;
  /** Sous-titre de l'en-tête (« Non lues : 3 »). */
  countLabel?: string;
  items: NotificationItem[];
  /** Repère accessible d'une notification non lue (« Non lue »). */
  unreadLabel: string;
  emptyLabel: string;
  /** Message à la place de la liste (chargement, erreur de chargement). */
  status?: string;
  /** Message bref sous l'en-tête (erreur d'une action). */
  notice?: string;
  markAllLabel?: string;
  markAllDisabled?: boolean;
  onMarkAll?: () => void;
  seeAllLabel?: string;
  onSeeAll?: (e: React.MouseEvent) => void;
  onOpen?: (id: string, e: React.MouseEvent) => void;
  onClose?: () => void;
}

export function NotificationsPanel(p: NotificationsPanelProps) {
  const liste = React.useRef<HTMLUListElement | null>(null);
  const clavier = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { p.onClose?.(); return; }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const boutons = Array.from(liste.current?.querySelectorAll<HTMLElement>('.agx-notif') || []);
    const i = boutons.indexOf(document.activeElement as HTMLElement);
    if (!boutons.length || i < 0) return;
    const n = boutons.length;
    const j = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
    e.preventDefault();
    boutons[j]?.focus();
  };
  const items = p.items || [];
  const corps = p.status
    ? h('p', { className: 'agx-notifs-vide', role: 'status' }, p.status)
    : items.length === 0
      ? h('p', { className: 'agx-notifs-vide' }, h(Icon, { name: 'bell', size: 20 }), p.emptyLabel)
      : h('ul', { className: 'agx-notifs-liste', ref: liste }, items.map((n) =>
        h('li', { key: n.id },
          h('button', {
            type: 'button', className: cx('agx-notif', n.unread && 'agx-notif-nonlue'),
            onClick: (e: React.MouseEvent) => p.onOpen?.(n.id, e),
          },
          h('span', { className: 'agx-notif-ico', 'aria-hidden': true }, h(Icon, { name: n.icon, size: 18 })),
          h('span', { className: 'agx-notif-txt' },
            h('span', { className: 'agx-notif-msg' }, n.message),
            h('span', { className: 'agx-notif-meta' },
              n.author ? h('span', null, n.author) : null,
              h('time', { dateTime: n.dateTime, title: n.dateLabel }, n.time),
              n.entity ? h('span', { className: 'ag-mono' }, n.entity) : null)),
          n.unread ? h('span', { className: 'agx-notif-point', role: 'img', 'aria-label': p.unreadLabel, title: p.unreadLabel }) : null))));
  return h('section', { className: cx('agx-notifs', 'ag-glass', 'ag-glass-strong'), role: 'dialog', 'aria-label': p.title, onKeyDown: clavier },
    h('header', { className: 'agx-notifs-head' }, h('b', null, p.title), p.countLabel ? h('span', null, p.countLabel) : null),
    p.notice ? h('p', { className: 'agx-notifs-avis', role: 'alert' }, p.notice) : null,
    corps,
    p.markAllLabel || p.seeAllLabel ? h('footer', { className: 'agx-notifs-pied' },
      p.markAllLabel ? h('button', { type: 'button', className: 'ag-btn ag-btn-quiet ag-btn-sm', disabled: p.markAllDisabled, onClick: p.onMarkAll },
        h(Icon, { name: 'check', size: 16 }), p.markAllLabel) : null,
      p.seeAllLabel ? h('button', { type: 'button', className: 'ag-btn ag-btn-secondary ag-btn-sm', onClick: p.onSeeAll },
        p.seeAllLabel, h(Icon, { name: 'arrow', size: 16, className: 'agx-sens' })) : null) : null);
}
